import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { getPublishedNews } from '../services/adminService';
import { NewsItem } from '../types/news';
import ModernBackground from '../components/common/ModernBackground';

const withAlpha = (hexColor: string, alpha: number) => {
  const hex = hexColor.replace('#', '');
  const normalized = hex.length === 3
    ? hex.split('').map(char => `${char}${char}`).join('')
    : hex;
  const bigint = parseInt(normalized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const isShadowTheme = (theme: any) => theme.name === 'shadowGrid';
const isModernTheme = (theme: any) => theme.name === 'modernWarfare';
const isNightOps = (theme: any) => theme.name === 'nightOps';

const cardBorder = (theme: any) => theme.profile?.cards?.border || theme.colors.borderLight;

const PageBackground = styled.div<{ $shadow?: boolean }>`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme.profile.pageBackground === 'transparent'
      ? 'transparent'
      : theme.gradients.background || theme.colors.background};
  padding: 20px;
  padding-top: 100px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding-top: 90px;
    padding: 10px;
    padding-top: 90px;
  }
`;

const DottedOverlay = styled.div`
  display: none;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  color: ${({ theme }) => theme.colors.text};
  position: relative;
  z-index: 3;
  font-family: ${({ theme }) => {
    if (theme.name === 'shadowGrid') return "'Courier New', monospace";
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return 'inherit';
  }};
  
  @media (max-width: 1240px) {
    max-width: 95%;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 0 10px 0;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 0 0 15px ${({ theme }) => withAlpha(theme.colors.primary, 0.7)};
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "inherit"};

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 15px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.secondary};
  margin: 0;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.4;
    padding: 0 10px;
  }
`;

const MobileFilterHeader = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }
`;

const MobileFilterButton = styled.button`
  padding: 12px 24px;
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => isShadowTheme(theme) ? 'white' : '#00121f'};
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
  }
`;

const FilterOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  z-index: 998;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileFilterPanel = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 320px;
  height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  border-right: 2px solid ${({ theme }) => theme.colors.primary};
  padding: 80px 20px 20px;
  transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
  transition: transform 0.3s ease;
  z-index: 999;
  overflow-y: auto;
  
  @media (min-width: 769px) {
    display: none;
  }
  
  @media (max-width: 320px) {
    width: 100%;
  }
`;

const MobileFilterHeader2 = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const MobileFilterTitle = styled.h3`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
  text-transform: uppercase;
`;

const MobileFilterClose = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 20px;
  cursor: pointer;
  padding: 5px;
  
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const FilterBar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const CategoryFilter = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const CategoryButton = styled.button<{ active: boolean }>`
  background: ${props => props.active 
    ? props.theme.colors.primary 
    : props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.primary};
  border-radius: 8px;
  color: ${props => props.active ? (isShadowTheme(props.theme) ? '#000000' : '#00121f') : props.theme.colors.primary};
  font-size: 14px;
  font-weight: bold;
  padding: 10px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : "inherit"};
  
  &:hover {
    background: ${props => props.active 
      ? props.theme.colors.primary
      : props.theme.colors.surfaceHover};
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 0 15px ${({ theme }) => withAlpha(theme.colors.primary, 0.4)};
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 12px 16px;
    text-align: left;
    width: 100%;
  }
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 25px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-bottom: 30px;
  }
`;

const NewsCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 12px;
  padding: 0;
  transition: all 0.3s ease;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  backdrop-filter: none;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    transform: translateY(-3px);
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 8px 25px ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
  }
`;

const NewsImage = styled.div<{ imageUrl?: string }>`
  height: 200px;
  background-image: ${props => props.imageUrl ? `url(${props.imageUrl})` : `linear-gradient(135deg, ${withAlpha(props.theme.colors.primary, 0.1)}, ${withAlpha(props.theme.colors.secondary, 0.1)})`};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 12px 12px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
  font-size: 14px;
  
  @media (max-width: 768px) {
    height: 200px;
  }
`;

const NewsContent = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 10px;
`;

const NewsHeader = styled.div`
  margin-bottom: 10px;
`;

const NewsCategory = styled.div`
  display: inline-block;
  padding: 4px 12px;
  background: ${({ theme }) => withAlpha(theme.colors.primary, 0.2)};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 11px;
  text-transform: uppercase;
  margin-bottom: 10px;
  border-radius: 4px;
  font-weight: bold;
`;

const NewsTitle = styled.h3`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 0 10px 0;
  line-height: 1.2;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "inherit"};
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const NewsDate = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 10px;
`;

const NewsExcerpt = styled.div`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
  margin-bottom: 15px;
  flex: 1;
  
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const NewsFooter = styled.div`
  margin-top: auto;
`;

const ReadMoreLink = styled(Link)`
  display: inline-block;
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: 6px;
  color: ${({ theme }) => isShadowTheme(theme) ? 'white' : '#00121f'};
  font-size: 0.9rem;
  font-weight: bold;
  padding: 8px 16px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
  text-transform: uppercase;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 40px;
  flex-wrap: wrap;
`;

const PaginationButton = styled.button<{ active?: boolean }>`
  background: ${props => props.active 
    ? props.theme.colors.primary 
    : props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.primary};
  border-radius: 6px;
  color: ${props => props.active ? (isShadowTheme(props.theme) ? '#000000' : '#00121f') : props.theme.colors.primary};
  font-size: 14px;
  font-weight: bold;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active 
      ? props.theme.colors.primary
      : props.theme.colors.surfaceHover};
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 15px ${({ theme }) => withAlpha(theme.colors.primary, 0.4)};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingText = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.2rem;
  margin: 40px 0;
`;

const ErrorText = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.error};
  font-size: 1.2rem;
  margin: 40px 0;
  padding: 20px;
  background: ${({ theme }) => withAlpha(theme.colors.error, 0.1)};
  border: 1px solid ${({ theme }) => theme.colors.error};
  border-radius: 8px;
`;

const NoNewsText = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 1.2rem;
  margin: 60px 0;
  
  h3 {
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 10px;
    font-size: 1.5rem;
  }
`;

const NewsIdBadge = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(0, 0, 0, 0.7);
  padding: 4px 8px;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 11px;
  font-weight: bold;
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
`;

const IntelPage: React.FC = () => {
  const theme = useTheme();
  const isShadowGrid = theme.name === 'shadowGrid';
  const isModernWarfare = theme.name === 'modernWarfare';
  const isNightOpsMode = theme.name === 'nightOps';

  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentCategory, setCurrentCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState<boolean>(false);

  useEffect(() => {
    fetchNews();
  }, [currentCategory, currentPage]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getPublishedNews(currentPage, 9, currentCategory);
      setNewsItems(response.news);
      setTotalPages(Math.ceil(response.totalCount / 9));
    } catch (err) {
      console.error('Error loading news:', err);
      setError('Failed to load news. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category: string) => {
    setCurrentCategory(category);
    setCurrentPage(1);
    setIsMobileFilterOpen(false); // Закрываем мобильное меню при выборе категории
  };

  const toggleMobileFilter = () => {
    setIsMobileFilterOpen(!isMobileFilterOpen);
  };

  const closeMobileFilter = () => {
    setIsMobileFilterOpen(false);
  };

  // Format date as "MM/DD/YYYY"
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Function to truncate text to a specific number of words
  const truncateText = (text: string, maxWords = 30) => {
    const words = text.split(' ');
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  // Function to get category name
  const getCategoryName = (category: string): string => {
    switch(category) {
      case 'General': return 'General';
      case 'Updates': return 'Updates';
      case 'Events': return 'Events';
      case 'Announcements': return 'Announcements';
      case 'Guides': return 'Guides';
      default: return category;
    }
  };

  // Компонент фильтров для переиспользования
  const FilterContent = () => (
    <CategoryFilter>
      <CategoryButton
        active={currentCategory === ''}
        onClick={() => handleCategoryChange('')}
      >
        All News
      </CategoryButton>
      <CategoryButton
        active={currentCategory === 'General'}
        onClick={() => handleCategoryChange('General')}
      >
        General
      </CategoryButton>
      <CategoryButton
        active={currentCategory === 'Updates'}
        onClick={() => handleCategoryChange('Updates')}
      >
        Updates
      </CategoryButton>
      <CategoryButton
        active={currentCategory === 'Events'}
        onClick={() => handleCategoryChange('Events')}
      >
        Events
      </CategoryButton>
      <CategoryButton
        active={currentCategory === 'Announcements'}
        onClick={() => handleCategoryChange('Announcements')}
      >
        Announcements
      </CategoryButton>
      <CategoryButton
        active={currentCategory === 'Guides'}
        onClick={() => handleCategoryChange('Guides')}
      >
        Guides
      </CategoryButton>
    </CategoryFilter>
  );

  return (
    <PageBackground $shadow={isShadowGrid}>
      {isNightOpsMode && <DottedOverlay />}
      {isModernWarfare && <ModernBackground />}
      <Container>
        <Header>
          <Title>Intel Hub</Title>
          <Subtitle>Stay informed with the latest news, updates, and tactical insights</Subtitle>
        </Header>
        
        <MobileFilterHeader>
          <MobileFilterButton onClick={toggleMobileFilter}>
            Categories
          </MobileFilterButton>
          <div style={{ fontSize: '14px', color: theme.colors.primary }}>
            {currentCategory ? getCategoryName(currentCategory) : 'All News'}
          </div>
        </MobileFilterHeader>
        
        <FilterBar>
          <FilterContent />
        </FilterBar>
        
        {loading ? (
          <LoadingText>Loading news...</LoadingText>
        ) : error ? (
          <ErrorText>{error}</ErrorText>
        ) : newsItems.length === 0 ? (
          <NoNewsText>
            <h3>No News Available</h3>
            <p>There are currently no news articles in this category.</p>
          </NoNewsText>
        ) : (
          <NewsGrid>
            {newsItems.map((newsItem, index) => (
              <NewsCard key={newsItem.id}>
                <NewsIdBadge>
                  #{(currentPage - 1) * 9 + index + 1}
                </NewsIdBadge>
                
                <Link to={`/intel/${newsItem.id}`} style={{ textDecoration: 'none', color: 'inherit', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <NewsImage imageUrl={
                    newsItem.imageUrls && newsItem.imageUrls.length > 0 
                      ? newsItem.imageUrls[0] 
                      : newsItem.imageUrl
                  }>
                    {!newsItem.imageUrls?.length && !newsItem.imageUrl && 'No Image Available'}
                  </NewsImage>
                  
                  <NewsContent>
                    <NewsHeader>
                      <NewsCategory>{getCategoryName(newsItem.category)}</NewsCategory>
                      <NewsTitle>{newsItem.title}</NewsTitle>
                      <NewsDate>{formatDate(newsItem.publishedAt || newsItem.createdAt)}</NewsDate>
                    </NewsHeader>
                    <NewsExcerpt>{truncateText(newsItem.content)}</NewsExcerpt>
                    <NewsFooter>
                      <ReadMoreLink to={`/intel/${newsItem.id}`} onClick={(e) => e.stopPropagation()}>
                        Read More
                      </ReadMoreLink>
                    </NewsFooter>
                  </NewsContent>
                </Link>
              </NewsCard>
            ))}
          </NewsGrid>
        )}
        
        {totalPages > 1 && (
          <Pagination>
            {Array.from({ length: totalPages }, (_, i) => (
              <PaginationButton
                key={i}
                active={currentPage === i + 1}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </PaginationButton>
            ))}
          </Pagination>
        )}
      </Container>

      {/* Mobile Filter Panel */}
      <FilterOverlay $isOpen={isMobileFilterOpen} onClick={closeMobileFilter} />
      <MobileFilterPanel $isOpen={isMobileFilterOpen}>
        <MobileFilterHeader2>
          <MobileFilterTitle>News Categories</MobileFilterTitle>
          <MobileFilterClose onClick={closeMobileFilter}>✕</MobileFilterClose>
        </MobileFilterHeader2>
        <FilterContent />
      </MobileFilterPanel>
    </PageBackground>
  );
};

export default IntelPage;
